import { METHODS } from "http";
import { AccRequest } from "../interfaces/AccRequest";
import { AccountObject } from "../interfaces/Administration/AccountObject";
import { CompanyCode } from "../interfaces/CompanyCode";
import { Group } from "../interfaces/Group";
import { ReportCode } from "../interfaces/ReportCode";
import { ReqSrchParameters } from "../interfaces/ReqSrchParameters";
import { ReviewSetup } from "../interfaces/ReviewSetup";
import { SuperUser } from "../interfaces/SuperUser";
import { deleteData, fetchData, fetchDataWithParams, postAttachData, postData } from "./api.service";
import { Approval } from "../interfaces/Approval";
import { debug } from "console";
import { Employee } from "../interfaces/Employee";
import { BusinessUnit } from "../interfaces/BusinessUnit";
import { OpenRequest } from "../interfaces/OpenRequest";
import { CCRequestFormValue } from "../interfaces/CCRequestFormValues";
import { AccRequestData, accRequestDetail, AccRequestObject } from "../interfaces/AccRequestData";
import { PendingAction } from "../interfaces/PendingAction";
import { AccReqFormValues } from "../interfaces/AccReqFormValues";
import { CCRequest, CCRequestObject } from "../interfaces/ccRequest";
import { appInsights } from "../../core/services/insight.service";
import { User } from "@auth0/auth0-react";
import { JdCompany } from "../interfaces/JdCompany";

export const fetchCompanyCodes = async (userProfile:User): Promise<CompanyCode[]> => {
  try {
    const response = await fetchData<CompanyCode[]>("CCRequests/company-code",userProfile);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchCompanyCodesFailed", properties: { error: error } });
    throw error;
  }
};

export const FetchSubsidiariesByObject = async (businessObject: string): Promise<AccRequestData[]> => {
  try {
    const response = await fetchData<AccRequestData[]>(`AccRequests/subsidiaries-by-object/${businessObject}`);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchSubsidiariesByObjectFailed", properties: { error: error } });
    throw error;
  }
};

export const fetchReportCodes = async (
  drsy: string,
  drrt: string
): Promise<ReportCode[]> => {
  try {
    const response = await fetchDataWithParams<ReportCode[]>(
      "CCRequests/report-code",
      {
        params: {
          drsy: drsy,
          drrt: drrt,
        },
      }
    );
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchReportCodesFailed", properties: { error: error } });
    throw error;
  }
};
export const fetchBusinessReportCode = async (
  drsy: string,
  drrt: string
): Promise<ReportCode[]> => {
  try {
    const response = await fetchDataWithParams<ReportCode[]>(
      "CCRequests/businessreport-code",
      {
        params: {
          drsy: drsy,
          drrt: drrt,
        },
      }
    );
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchReportCodesFailed", properties: { error: error } });
    throw error;
  }
};

export const fetchSearchAccRequest = async (
  srchParams: ReqSrchParameters
): Promise<AccRequest[]> => {
  try {
    const response = await fetchDataWithParams<AccRequest[]>(`AccRequests/search-requests`,
      {
        params: srchParams,
      }
    );
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchSearchAccRequestFailed", properties: { error: error } });
    throw error;
  }
};

export const fetchAccReqDetails = async (
  requestId: string
): Promise<AccRequest[]> => {
  try {
    const response = await fetchData<AccRequest[]>(
      `AccRequests/request/${requestId}`
    );
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchAccReqDetailsFailed", properties: { error: error } });
    throw error;
  }
};

export const fetchSuperUsersData = async (userProfile:User): Promise<SuperUser[]> => {
  try {
    const response = await fetchData<SuperUser[]>(`SuperAdminSetup/super-users`, userProfile);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchSuperUsersDataFailed", properties: { error: error } });
    throw error;
  }
};

export const SaveSuperUsers = async (data: SuperUser,userProfile:User): Promise<SuperUser> => {
  try {
    const response = await postData<SuperUser>(`SuperAdminSetup/super-admin`, data, userProfile);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "SaveSuperUsersFailed", properties: { error: error } });
    throw error;
  }
};

export const DeleteSuperUsers = async (emailID: string,userProfile:User): Promise<void> => {
  try {
    const response = await deleteData(`SuperAdminSetup/delete-super-admin/${emailID}`, userProfile);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "DeleteSuperUsersFailed", properties: { error: error } });
    throw error;
  }
};

export const FetchReviewGroup = async (userProfile:User): Promise<Group[]> => {
  try {
    const response = await fetchData<Group[]>(`ReviewerSetup/appr-groups`,userProfile);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchReviewGroupFailed", properties: { error: error } });
    throw error;
  }
};

export const FetchReviewUsers = async (userProfile:User): Promise<ReviewSetup[]> => {
  try {
    const response = await fetchData<ReviewSetup[]>(`ReviewerSetup/all-approvers`,userProfile);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchReviewUsersFailed", properties: { error: error } });
    throw error;
  }
};

export const SaveReviewUsers = async (data: Approval,userProfile:User): Promise<Approval> => {
  try {
    const response = await postData<Approval>(`ReviewerSetup/reviewer-setup`, data, userProfile);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "SaveReviewUsersFailed", properties: { error: error } });
    throw error;
  }
};

export const DeleteReviewUsers = async (emailID: string, userProfile:User): Promise<void> => {
  try {
    const response = await deleteData(`ReviewerSetup/delete-reviewer-setup/${emailID}`,userProfile);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "DeleteReviewUsersFailed", properties: { error: error } });
    throw error;
  }
};

export const fetchEmpDetails = async (email: string): Promise<Employee[]> => {
  try {
    const response = await fetchData<Employee[]>(`Employees/search-employee/${email}`);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchEmpDetailsFailed", properties: { error: error } });
    throw error;
  }
};

export const FetchPendingAction = async (): Promise<Group[]> => {
  try {
    const response = await fetchData<Group[]>(`AccRequests/pending`);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchPendingActionFailed", properties: { error: error } });
    throw error;
  }
};

export const FetchCompany = async (companyCodeNumber: string): Promise<JdCompany> => {
  try {
    const response = await fetchData<JdCompany>(`CCRequests/location-code-by-company-code/${companyCodeNumber}`);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchCompanyFailed", properties: { error: error } });
    throw error;
  }
};

export const fetchOpenRequestsData = async (email: string): Promise<OpenRequest[]> => {
  try {
    const response = await fetchData<OpenRequest[]>(`AccRequests/open-requests/${email}`);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchOpenRequestsDataFailed", properties: { error: error } });
    throw error;
  }
};

export const fetchPendingActionsData = async (email: string): Promise<PendingAction[]> => {
  try {
    const response = await fetchData<PendingAction[]>(`AccRequests/pending-my-action/${email}`);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchPendingActionsDataFailed", properties: { error: error } });
    throw error;
  }
};

export const SaveCCRequest = async (data: CCRequestFormValue): Promise<CCRequestFormValue> => {
  try {
    const response = await postData<CCRequestFormValue>(`CCRequests/insert-ccrequest`, data);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "SaveCCRequestFailed", properties: { error: error } });
    throw error;
  }
};

export const SaveCCRequestDetails = async (data: CCRequestFormValue): Promise<CCRequestFormValue> => {
  try {
    const response = await postData<CCRequestFormValue>(`CCReqDetailss/insert-CCrequestDetails`, data);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "SaveCCRequestDetailsFailed", properties: { error: error } });
    throw error;
  }
};

export const SaveApprovalRequest = async (data: CCRequestFormValue): Promise<CCRequestFormValue> => {
  try {
    const response = await postData<CCRequestFormValue>(`CCApprovals/insert-approvalrequest`, data);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "SaveApprovalRequestFailed", properties: { error: error } });
    throw error;
  }
};

export const SaveCCAttachment = async (formdata?: any) => {
  try {
    const response = await postAttachData(`CCRequests/upload-file/${formdata.requestid}`, formdata);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "SaveCCAttachmentFailed", properties: { error: error } });
    throw error;
  }
};

export const SaveAccAttachment = async (formdata?: any) => {
  try {
    const response = await postAttachData(`AccRequests/upload-file/${formdata.requestid}`, formdata);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "SaveAccAttachmentFailed", properties: { error: error } });
    throw error;
  }
};

export const SaveACCRequest = async (data: AccReqFormValues): Promise<AccReqFormValues> => {
  try {
    const response = await postData<AccReqFormValues>(`AccRequests/insert-accrequest`, data);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "SaveACCRequestFailed", properties: { error: error } });
    throw error;
  }
};

export const SaveACCRequestDetails = async (data: AccReqFormValues): Promise<AccReqFormValues> => {
  try {
    const response = await postData<AccReqFormValues>(`AccRequests/insert-AccrequestDetails`, data);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "SaveACCRequestDetailsFailed", properties: { error: error } });
    throw error;
  }
};

export const SaveAccApprovalRequest = async (data: AccReqFormValues): Promise<AccReqFormValues> => {
  try {
    const response = await postData<AccReqFormValues>(`AccRequests/insert-Accapprovalrequest`, data);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "SaveAccApprovalRequestFailed", properties: { error: error } });
    throw error;
  }
};

export const fetchAccReqObjectDetails = async (
  requestId: string
): Promise<AccRequestObject[]> => {
  try {
    const response = await fetchData<AccRequestObject[]>(
      `AccRequests/request-object/${requestId}`
    );
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchAccReqObjectDetailsFailed", properties: { error: error } });
    throw error;
  }
};

export const fetchCcReqDetails = async (
  requestId: string, email: string
): Promise<accRequestDetail[]> => {
  try {
    const response = await fetchData<accRequestDetail[]>(
      `CCRequests/cc-request-detaill/${requestId}/${email}`
    );
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchCcReqDetailsFailed", properties: { error: error } });
    throw error;
  }
};

export const fetchCcReqObjectDetails = async (
  requestId: string
): Promise<CCRequestObject[]> => {
  try {
    const response = await fetchData<CCRequestObject[]>(
      `CCReqDetailss/request-object/${requestId}`
    );
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchCcReqObjectDetailsFailed", properties: { error: error } });
    throw error;
  }
};

export const fetchAccRequestDetails = async (
  requestId: string, email:string
): Promise<accRequestDetail[]> => {
  try {
    const response = await fetchData<accRequestDetail[]>(
      `AccRequests/acc-request-detail/${requestId}/${email}` // Assuming email is optional
    );
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "FetchAccReqDetailsFailed", properties: { error: error } });
    throw error;
  }
};


export const updateAccApprovalRequest = async (data: any,email:string): Promise<any> => {
  try {
    const response = await postData<any>(`AccRequests/acc-request/update-approval/${email}`, data);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "updateAccApprovalRequestFailed", properties: { error: error } });
    throw error;
  }
};

export const updateccApprovalRequest = async (data: any,email:string): Promise<any> => {
  try {
    const response = await postData<any>(`CCRequests/cc-request/update-approval/${email}`, data);
    return response;
  } catch (error: any) {
    appInsights.trackEvent({ name: "updateccApprovalRequestFailed", properties: { error: error } });
    throw error;
  }
};